import { TASK_ASSIGNEE_TYPES, TASK_TYPES } from 'resources/task/task.constants';
import { MILESTONE_TYPES, MILESTONE_STATUSES } from 'resources/milestone/milestone.constants';
import { LISTEDKIT_TYPES, ONBOARDING_TASK_CONTENT, DEFAULT_NAMES } from 'resources/listedkit/listedkit.constants';
import { COMMON_TEMPLATE_TITLES } from 'resources/template/template.constants';
import { COMMON_COMPUTED_FIELDS } from 'components/transaction/advancedFields/index.helpers';

export const getDefaultKitTemplates = (defaultEmailTemplates) => {
  const getEmailTemplateIdByTitle = (emailTemplateTitle) => {
    const emailTemplate = defaultEmailTemplates.find((template) => template.title === emailTemplateTitle);
    if (!emailTemplate) {
      throw new Error(`Email template with title ${emailTemplateTitle} not found`);
    }
    return emailTemplate.id;
  };

  const buyerPreContractMilestones = [
    {
      name: 'New Buyer Onboarding',
      type: MILESTONE_TYPES.PRE_CONTRACT,
      due_in_days: null,
      editable: false,
      tasks: [{
        content: ONBOARDING_TASK_CONTENT.COMPLETE_HOME_SEARCH_FORM,
        assignee_type: TASK_ASSIGNEE_TYPES.BUYER,
        predefined: false,
      }],
      status: MILESTONE_STATUSES.ACTIVE,
    },
    {
      name: 'Pre-Approval',
      type: MILESTONE_TYPES.PRE_CONTRACT,
      due_in_days: null,
      editable: true,
      tasks: [{
        content: 'Submit mortgage application',
        assignee_type: TASK_ASSIGNEE_TYPES.BUYER,
      }, {
        content: 'Send Pre-Approval Letter to Agent',
        assignee_type: TASK_ASSIGNEE_TYPES.BUYER,
      }, {
        content: 'Sign Buyer-Agency Agreement',
        assignee_type: TASK_ASSIGNEE_TYPES.BUYER,
      }, {
        content: 'Sign RECAD Forms',
        assignee_type: TASK_ASSIGNEE_TYPES.BUYER,
      }, {
        content: 'Review Loan Estimate',
        assignee_type: TASK_ASSIGNEE_TYPES.BUYERS_AGENT,
      }, {
        content: 'Pre-Approval Letter',
        assignee_type: TASK_ASSIGNEE_TYPES.BUYERS_AGENT,
        type: TASK_TYPES.DOCUMENT_REQUEST,
      }, {
        content: 'RECAD Forms',
        assignee_type: TASK_ASSIGNEE_TYPES.BUYERS_AGENT,
        type: TASK_TYPES.DOCUMENT_REQUEST,
      }, {
        content: 'Buyer-Agency',
        assignee_type: TASK_ASSIGNEE_TYPES.BUYERS_AGENT,
        type: TASK_TYPES.DOCUMENT_REQUEST,
      }],
      status: MILESTONE_STATUSES.ACTIVE,
    },
    {
      name: 'Home Search/Make Offer',
      type: MILESTONE_TYPES.PRE_CONTRACT,
      due_in_days: null,
      editable: true,
      tasks: [{
        content: 'Set up home search',
        assignee_type: TASK_ASSIGNEE_TYPES.BUYERS_AGENT,
      }, {
        content: 'Review Loan Estimate',
        assignee_type: TASK_ASSIGNEE_TYPES.BUYERS_AGENT,
      }, {
        content: 'Contact Listing Agent to discuss property',
        assignee_type: TASK_ASSIGNEE_TYPES.BUYERS_AGENT,
      }, {
        content: 'Draft Offer and Send to Buyer',
        assignee_type: TASK_ASSIGNEE_TYPES.BUYERS_AGENT,
      }, {
        content: 'Sign Offer',
        assignee_type: TASK_ASSIGNEE_TYPES.BUYER,
      }, {
        content: 'Submit offer to Listing Agent',
        assignee_type: TASK_ASSIGNEE_TYPES.BUYERS_AGENT,
      }, {
        content: 'Buyer Net Sheet',
        assignee_type: TASK_ASSIGNEE_TYPES.BUYERS_AGENT,
        type: TASK_TYPES.DOCUMENT_REQUEST,
      }, {
        content: 'Offer Letter',
        assignee_type: TASK_ASSIGNEE_TYPES.BUYERS_AGENT,
        type: TASK_TYPES.DOCUMENT_REQUEST,
      }],
      status: MILESTONE_STATUSES.ACTIVE,
    }];

  const buyerUnderContractMilestones = [
    {
      name: 'Process File',
      type: MILESTONE_TYPES.UNDER_CONTRACT,
      due_in_days: 1,
      is_after_action: true,
      due_relative_date: COMMON_COMPUTED_FIELDS.UNDER_CONTRACT_DATE.NAME,
      editable: true,
      status: MILESTONE_STATUSES.ACTIVE,
      tasks: [{
        content: 'Review all documents for accuracy-signatures/initials',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
      }, {
        content: 'Acceptance date/closing date',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
      }, {
        content: 'Retrieve missing docs/signatures',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
      }, {
        content: 'Review and collect addendums',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
      }, {
        content: 'Check for FHA/VA amend',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
      }, {
        content: 'Check if lead based paint disclosure is needed',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        description: 'Federal Law requires a Lead-Based Paint Disclosure for any residential property built before 1978.',
      }, {
        content: 'Submit to compliance within 3 business days of acceptance',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
      }, {
        content: `Send ${COMMON_TEMPLATE_TITLES.ALL_PARTIES}`,
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.EMAIL_TEMPLATE,
        template_id: getEmailTemplateIdByTitle(COMMON_TEMPLATE_TITLES.ALL_PARTIES),
        is_hidden_from_client: true,
      }, {
        content: `Send ${COMMON_TEMPLATE_TITLES.WELCOME_THE_CLIENT}`,
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.EMAIL_TEMPLATE,
        template_id: getEmailTemplateIdByTitle(COMMON_TEMPLATE_TITLES.WELCOME_THE_CLIENT),
        is_hidden_from_client: true,
      }, {
        content: 'Intro calls to co op agent and lender',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
      }, {
        content: 'Contract',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.DOCUMENT_REQUEST,
        is_hidden_from_client: true,
      }, {
        content: 'FHA/VA Amend',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.DOCUMENT_REQUEST,
        is_optional: true,
        is_hidden_from_client: true,
      }, {
        content: 'Lead Based Paint Disclosure',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.DOCUMENT_REQUEST,
        is_optional: true,
        is_hidden_from_client: true,
      }],
    },
    {
      name: 'Home Inpection',
      type: MILESTONE_TYPES.UNDER_CONTRACT,
      due_in_days: 14,
      is_after_action: true,
      due_relative_date: COMMON_COMPUTED_FIELDS.UNDER_CONTRACT_DATE.NAME,
      editable: true,
      status: MILESTONE_STATUSES.ACTIVE,
      tasks: [{
        content: 'Confirm buyer has scheduled home inspection',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
      }, {
        content: 'Notify listing agent of scheduled time',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
      }, {
        content: 'Send reminder to agent 48 hours prior',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
      }, {
        content: 'Report received by buyer',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
      }, {
        content: 'Repair addendum prepared by agent',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
      }, {
        content: 'Repair addendum sent for signatures',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
      }, {
        content: 'Repair addendum received/signed by all parties (set due date)',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
      }, {
        content: 'All repairs completed',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
      }, {
        content: 'Request invoices/receipts from seller\'s agent',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
      }, {
        content: `Send ${COMMON_TEMPLATE_TITLES.UPCOMING_INSPECTION_REMINDER}`,
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.EMAIL_TEMPLATE,
        template_id: getEmailTemplateIdByTitle(COMMON_TEMPLATE_TITLES.UPCOMING_INSPECTION_REMINDER),
        description: 'Send Buyers Agent reminder of upcoming Home Inspection',
        is_hidden_from_client: true,
      }],
    },
    {
      name: 'Termite Inspection',
      type: MILESTONE_TYPES.UNDER_CONTRACT,
      due_in_days: 20,
      is_after_action: true,
      due_relative_date: COMMON_COMPUTED_FIELDS.UNDER_CONTRACT_DATE.NAME,
      editable: true,
      status: MILESTONE_STATUSES.ACTIVE,
      tasks: [{
        content: 'Request Wood Infestation Report (WIR) from sellers agent',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
      }, {
        content: 'Send report to lender',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
      }],
    },
    {
      name: 'Appraisal',
      type: MILESTONE_TYPES.UNDER_CONTRACT,
      due_in_days: 21,
      is_after_action: true,
      due_relative_date: COMMON_COMPUTED_FIELDS.UNDER_CONTRACT_DATE.NAME,
      editable: true,
      status: MILESTONE_STATUSES.ACTIVE,
      tasks: [{
        content: 'Appraisal order by lender',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Confirm Appraisal Inspection has been completed',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Appraisal report received by lender',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Appraisal review completed by lender',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'After the Appraisal, notify Listing Agent',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'If repairs required, update transaction details',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        description: 'If repairs are required following the appraisal, update with any changes to the transaction following negotiations.',
        is_hidden_from_client: true,
      }, {
        content: 'If repairs are required, create tasks/calendar events for repairs.',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        description: 'Create any tasks/calendar events for repair appointments.',
        is_hidden_from_client: true,
      }, {
        content: 'Appraisal Report',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.DOCUMENT_REQUEST,
        is_hidden_from_client: true,
      }],
    },
    {
      name: 'Closing Prep',
      type: MILESTONE_TYPES.UNDER_CONTRACT,
      due_in_days: 0,
      is_after_action: false,
      due_relative_date: COMMON_COMPUTED_FIELDS.CLOSING_DATE.NAME,
      editable: true,
      status: MILESTONE_STATUSES.ACTIVE,
      tasks: [{
        content: 'Schedule closing and add to calendar',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Order home warranty',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: `Send ${COMMON_TEMPLATE_TITLES.BUYER_CLOSING_INSTRUCTIONS}`,
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.EMAIL_TEMPLATE,
        template_id: getEmailTemplateIdByTitle(COMMON_TEMPLATE_TITLES.BUYER_CLOSING_INSTRUCTIONS),
        description: 'Send email to buyer with instructions to prepare for closing.',
        is_hidden_from_client: true,
      }, {
        content: 'Send Buyer confirmation',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Schedule Final Walkthrough with Buyer/Agent',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Request final CD from Closing Attorney',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'CD - Confirm commission and send to agent for review',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Confirm clear to close received from Lender',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'After clear to close received, notify Listing Agent',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }],
    },
    {
      name: 'Post Closing',
      type: MILESTONE_TYPES.AFTER_CLOSE,
      due_in_days: null,
      is_after_action: true,
      editable: true,
      status: MILESTONE_STATUSES.ACTIVE,
      tasks: [{
        content: 'Add invoice to Quickbooks',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'TC invoice paid',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Upload closing documents to compliance platform',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: `Send ${COMMON_TEMPLATE_TITLES.BUYER_POST_CLOSING_AGENT_REVIEW_REQUEST}`,
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.EMAIL_TEMPLATE,
        template_id: getEmailTemplateIdByTitle(COMMON_TEMPLATE_TITLES.BUYER_POST_CLOSING_AGENT_REVIEW_REQUEST),
        description: 'Send Buyer email to leave a review for Buyers Agent',
        is_hidden_from_client: true,
      }, {
        content: 'Send email to Lender with Congrats and Thank You',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Add Listing Agent to CRM as a new lead',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        due_in_days: 2,
        is_after_action: true,
        due_relative_date: COMMON_COMPUTED_FIELDS.CLOSING_DATE.NAME,
        description: `Listing Agents are excellent leads to grow your business. Make sure you take advantage of this opportunity by calling to emailing them to offer your services.
        
Pro Tip: Leave the Listing Agent a favorable review and use it as a talking point`,
        is_hidden_from_client: true,
      }, {
        content: 'Send email to Listing Agent with Congrats and Thank You',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }],
    }];

  const listingPreContractMilestones = [
    {
      name: 'Pre-Listing',
      type: MILESTONE_TYPES.PRE_CONTRACT,
      due_in_days: null,
      is_after_action: true,
      editable: true,
      status: MILESTONE_STATUSES.ACTIVE,
      tasks: [{
        content: 'Schedule Photoshoot',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Add Photoshoot to Calendar',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Sign ordered or placed in yard',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Send copy of Listing Agreement to Seller',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Lockbox on door?',
        assignee_type: TASK_ASSIGNEE_TYPES.SELLERS_AGENT,
      }, {
        content: 'Listing entered into MLS',
        assignee_type: TASK_ASSIGNEE_TYPES.SELLERS_AGENT,
      }, {
        content: "Update Showing Time to reflect Seller's Preferences",
        assignee_type: TASK_ASSIGNEE_TYPES.SELLERS_AGENT,
      }, {
        content: `Send ${COMMON_TEMPLATE_TITLES.SELLER_PHOTO_SHOOT_SCHEDULE}`,
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
        type: TASK_TYPES.EMAIL_TEMPLATE,
        template_id: getEmailTemplateIdByTitle(COMMON_TEMPLATE_TITLES.SELLER_PHOTO_SHOOT_SCHEDULE),
        description: 'Send this email informing the Seller that the photoshoot has been scheduled and reminders for the photoshoot. ADD TIME/DATE TO EMAIL, IF NOT USING GOOGLE CALENDAR',
      }, {
        content: 'Signed Listing Agreement',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
        type: TASK_TYPES.DOCUMENT_REQUEST,
      }],
    },
    {
      name: 'Active Listing',
      type: MILESTONE_TYPES.PRE_CONTRACT,
      due_in_days: null,
      is_after_action: true,
      editable: true,
      status: MILESTONE_STATUSES.ACTIVE,
      tasks: [{
        content: 'Order Pre-lim Title',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Reverse prospecting in MLS',
        assignee_type: TASK_ASSIGNEE_TYPES.SELLERS_AGENT,
      }, {
        content: 'Claim listing on Zillow',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Promote listing on Facebook and Instagram',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Change status to Active Listing',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
        description: 'Change the status of this transaction in ListedKit by selecting the Status Dropdown on the Tasks tab.',
      }],
    },
  ];

  const listingUnderContractMilestones = [
    {
      name: 'Process File',
      type: MILESTONE_TYPES.UNDER_CONTRACT,
      due_in_days: 2,
      is_after_action: true,
      due_relative_date: COMMON_COMPUTED_FIELDS.UNDER_CONTRACT_DATE.NAME,
      editable: true,
      status: MILESTONE_STATUSES.ACTIVE,
      tasks: [{
        content: `Send ${COMMON_TEMPLATE_TITLES.WELCOME_THE_CLIENT}`,
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.EMAIL_TEMPLATE,
        template_id: getEmailTemplateIdByTitle(COMMON_TEMPLATE_TITLES.WELCOME_THE_CLIENT),
        is_hidden_from_client: true,
      }, {
        content: 'Change listing to pending in MLS',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: `Send ${COMMON_TEMPLATE_TITLES.ALL_PARTIES}`,
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.EMAIL_TEMPLATE,
        template_id: getEmailTemplateIdByTitle(COMMON_TEMPLATE_TITLES.ALL_PARTIES),
        is_hidden_from_client: true,
      }, {
        content: 'Ensure all Key Dates and Details are filled/accurate',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        description: 'Navigate to the "Details" Tab of this transaction and ensure all important dates, contact information, and other key details for this transaction are entered. You can populate this data in Emails.',
      }, {
        content: 'Pre Approval/POF',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.DOCUMENT_REQUEST,
      }, {
        content: 'Executed Contract',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.DOCUMENT_REQUEST,
      }, {
        content: 'Counter Offer',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.DOCUMENT_REQUEST,
        is_optional: true,
      }, {
        content: 'Greensheet',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.DOCUMENT_REQUEST,
        is_hidden_from_client: true,
      }, {
        content: 'Earnest Money Receipt',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.DOCUMENT_REQUEST,
        is_optional: true,
        is_hidden_from_client: true,
      }],
    },
    {
      name: 'Confirm Home Inspection',
      type: MILESTONE_TYPES.UNDER_CONTRACT,
      due_in_days: null,
      is_after_action: true,
      editable: true,
      status: MILESTONE_STATUSES.ACTIVE,
      tasks: [{
        content: 'Add to Calendar',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        description: 'Create a Google Calendar Event and invite the Listing Agent and Seller(s).',
        is_hidden_from_client: true,
      }],
    },
    {
      name: 'Schedule Closing',
      type: MILESTONE_TYPES.UNDER_CONTRACT,
      due_in_days: null,
      is_after_action: true,
      editable: true,
      status: MILESTONE_STATUSES.ACTIVE,
      tasks: [{
        content: 'Confirm time with all parties',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Add to Calendar',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        description: 'Create a Google Calendar event and invite Listing Agent and Seller(s).',
        is_hidden_from_client: true,
      }],
    },
    {
      name: 'Appraisal',
      type: MILESTONE_TYPES.UNDER_CONTRACT,
      due_in_days: null,
      is_after_action: true,
      editable: true,
      status: MILESTONE_STATUSES.ACTIVE,
      tasks: [{
        content: 'Add Appraisal Date',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        description: 'You can add the Appraisal Date in the Details tab of the Transaction.',
        is_hidden_from_client: true,
      }, {
        content: 'Add Appraisal to Calendar',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Confirm Appraisal Ordered',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }],
    },
    {
      name: 'Title',
      type: MILESTONE_TYPES.UNDER_CONTRACT,
      due_in_days: null,
      is_after_action: true,
      editable: true,
      status: MILESTONE_STATUSES.ACTIVE,
      tasks: [{
        content: 'Obtain LLC docs if applicable',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Receive Title Commitment',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Send payoff authorization, release of info, and commission instructions.',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Copy of Title Commitment',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.DOCUMENT_REQUEST,
        is_hidden_from_client: true,
      }],
    },
    {
      name: 'Settlement Statement',
      type: MILESTONE_TYPES.UNDER_CONTRACT,
      due_in_days: null,
      is_after_action: true,
      editable: true,
      status: MILESTONE_STATUSES.ACTIVE,
      tasks: [{
        content: 'Receive Prelim Settlement Statement',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: `Send ${COMMON_TEMPLATE_TITLES.SELLER_SETTLEMENT_STATEMENT_EMAIL}`,
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.EMAIL_TEMPLATE,
        template_id: getEmailTemplateIdByTitle(COMMON_TEMPLATE_TITLES.SELLER_SETTLEMENT_STATEMENT_EMAIL),
        is_hidden_from_client: true,
      }, {
        content: 'Settlement Statement',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.DOCUMENT_REQUEST,
      }],
    },
    {
      name: 'Inspections/TRR',
      type: MILESTONE_TYPES.UNDER_CONTRACT,
      due_in_days: null,
      is_after_action: true,
      editable: true,
      status: MILESTONE_STATUSES.ACTIVE,
      tasks: [{
        content: 'Send Repair Requests to Seller',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Obtain bids as needed',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Send Seller’s response to Buyer’s repair requests',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Confirm repairs are complete',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Obtain and upload invoices & receipts',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Send a copy of invoices & receipts to agent',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Add Inspection Window to Calendar',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Send Wood Infestation Report (WIR) to Buyer\'s Side',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Inspection Report',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.DOCUMENT_REQUEST,
        is_hidden_from_client: true,
      }, {
        content: 'Repair Request',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.DOCUMENT_REQUEST,
        is_optional: true,
        is_hidden_from_client: true,
      }, {
        content: 'Executed Repair Request Form',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.DOCUMENT_REQUEST,
        is_optional: true,
        is_hidden_from_client: true,
      }],
    },
    {
      name: 'Pre-Closing',
      type: MILESTONE_TYPES.UNDER_CONTRACT,
      due_in_days: null,
      is_after_action: true,
      editable: true,
      status: MILESTONE_STATUSES.ACTIVE,
      tasks: [{
        content: 'Send invoices to buyer’s agent and title',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Confirm that final walkthrough is scheduled',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Confirm final walkthrough with seller',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Confirm clear to close with lender',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Confirm clear to close with title/escrow',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Invoices received from seller',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Remove sign, lockbox, and take key to closing',
        assignee_type: TASK_ASSIGNEE_TYPES.SELLERS_AGENT,
      }, {
        content: `Send ${COMMON_TEMPLATE_TITLES.SELLER_CLOSING_REMINDER_EMAIL}`,
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        type: TASK_TYPES.EMAIL_TEMPLATE,
        template_id: getEmailTemplateIdByTitle(COMMON_TEMPLATE_TITLES.SELLER_CLOSING_REMINDER_EMAIL),
        is_hidden_from_client: true,
      }],
    },
    {
      name: 'Post-Closing',
      type: MILESTONE_TYPES.AFTER_CLOSE,
      due_in_days: null,
      is_after_action: true,
      editable: true,
      status: MILESTONE_STATUSES.ACTIVE,
      tasks: [{
        content: 'Upload Closing Documents',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Update seller contact information in CRM',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Submit for Compliance',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
        description: "Upload to Brokerage's required Compliance platform, or use ListedKit's Review Feature to facilitate a compliance check.",
      }, {
        content: 'Change MLS to Sold',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Add Buyer Agent to CRM as a new lead',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
        description: `If you're an independent TC, Co-operating Agents are excellent leads to grow your business. Make sure you take advantage of this opportunity by calling to emailing them to offer your services. 

Pro Tip: Leave the Buyers Agent a favorable review and use it as a talking point`,
      }, {
        content: 'Send email to Lender with Congrats and Thank You',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }, {
        content: 'Add Co-operating Agent to CRM',
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
        description: `If you're an independent TC, Co-operating Agents are excellent leads to grow your business. Make sure you take advantage of this opportunity by calling to emailing them to offer your services. 

Pro Tip: Leave the Buyers Agent a favorable review and use it as a talking point`,
      }, {
        content: "Send email to Buyer's Agent with Congrats and Thank You",
        assignee_type: TASK_ASSIGNEE_TYPES.COORDINATOR,
        is_hidden_from_client: true,
      }],
    },
  ];

  return [{
    name: DEFAULT_NAMES.BLANK,
    type: LISTEDKIT_TYPES.BLANK,
  }, {
    name: DEFAULT_NAMES.BUYER_PRE_CONTRACT,
    type: LISTEDKIT_TYPES.BUYER_PRE_CONTRACT,
    milestones: buyerPreContractMilestones,
  }, {
    name: DEFAULT_NAMES.BUYER_UNDER_CONTRACT,
    type: LISTEDKIT_TYPES.BUYER_UNDER_CONTRACT,
    milestones: buyerUnderContractMilestones,
  }, {
    name: DEFAULT_NAMES.LISTING_PRE_CONTRACT,
    type: LISTEDKIT_TYPES.SELLER_PRE_CONTRACT,
    milestones: listingPreContractMilestones,
  }, {
    name: DEFAULT_NAMES.LISTING_UNDER_CONTRACT,
    type: LISTEDKIT_TYPES.SELLER_UNDER_CONTRACT,
    milestones: listingUnderContractMilestones,
  }, { // Ghost Kits
    type: LISTEDKIT_TYPES.BUYER_GHOST,
    isGhost: true,
  }, {
    type: LISTEDKIT_TYPES.SELLER_GHOST,
    isGhost: true,
  }];
};
